import formStyles from "./UserCreationForm.module.css"
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react"
import Dropzone from "react-dropzone"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import api from "../api"
import { ReactComponent as AddIcon } from "../assets/images/icons/ic-plus.svg"
import { ReactComponent as EditIcon } from "../assets/images/icons/ic-image-edit.svg"
import { ReactComponent as InsertImageIllustration } from "../assets/images/illustrations/il-insert-image.svg"
import { AccountStatus, AdminRole, ButtonStatus, CompanyAdminRole } from "../common/constants"
import { Each } from "../common/Each"
import { capitalize, sortUser } from "../utils"
import styles from "./Admins.module.css"
import Button from "./Button"
import AdminCard from "./cards/AdminCard"
import Dialog from "./dialogs/Dialog"
import ImageCropper from "./ImageCropper"
import Select from "./Select"
import TextInput from "./TextInput"
import StatusBadge from "./StatusBadge"
import Table from "./Table"

const Admins = forwardRef(({ orderBy, sortDirection }, ref) => {
    const { t } = useTranslation()
    const navigate = useNavigate()

    const [admins, setAdmins] = useState([])
    const [filteredAdmins, setFilteredAdmins] = useState([])
    const [adminsLoading, setAdminLoading] = useState([])

    const [open, setOpen] = useState(false)
    const [profilePic, setProfilePic] = useState(null)
    const [email, setEmail] = useState("")
    const [name, setName] = useState("")
    const [surname, setSurname] = useState("")
    const [status, setStatus] = useState(AccountStatus.Active)
    const [imgFile, setImgFile] = useState(null)
    const [cropMode, setCropMode] = useState(false)
    const [addStatus, setAddStatus] = useState(null)
    const [isAdminValid, setIsAdminValid] = useState(false)
    const [companies, setCompanies] = useState([])
    const [selectedCompany, setSelectedCompany] = useState(null)
    const companyRoleOptions = useMemo(() => {
        return [
            { id: 0, name: "Owner", value: CompanyAdminRole.Owner },
            { id: 1, name: "Admin", value: CompanyAdminRole.Admin },
            { id: 2, name: "Manager", value: CompanyAdminRole.Manager },
            { id: 3, name: "Analyst", value: CompanyAdminRole.Analyst },
            { id: 4, name: "Sales", value: CompanyAdminRole.Sales },
        ]
    }, [])
    const [selectedCompanyRole, setSelectedCompanyRole] = useState(null)

    const roleOptions = useMemo(() => {
        return [
            { id: 0, name: "Main", value: AdminRole.Main },
            { id: 1, name: "Sales", value: AdminRole.Sales },
            { id: 2, name: "Media", value: AdminRole.Media },
            { id: 3, name: "Nabila", value: AdminRole.Nabila },
            { id: 4, name: "Marketing", value: AdminRole.Marketing },
        ]
    }, [])
    const [role, setRole] = useState(roleOptions[0])

    const fetchCompanies = useCallback(async () => {
        try {
            const response = await api.get(`/admin/companies?page=0&per=500`)
            const mapped = (response?.companies || []).map((c) => ({
                id: c.id,
                value: c.id,
                name: c.name,
            }))
            setCompanies(mapped)
        } catch (e) {
            console.error(e)
        }
    }, [])

    const dropzoneRef = useRef(null)
    const getAdmins = async () => {
        setAdminLoading(true)
        try {
            const admins = await api.get(`/admin/admins`)
            admins.sort((a, b) => sortUser(a, b, orderBy, sortDirection))
            setAdmins(admins)
            setFilteredAdmins(admins)
        } catch (e) {
            console.error(e)
        }
        setAdminLoading(false)
    }
    useEffect(() => {
        getAdmins()
        fetchCompanies()
    }, [fetchCompanies])

    useEffect(() => {
        setAdmins(t => {
            t.sort((a, b) => sortUser(a, b, orderBy, sortDirection))
            return [...t]
        })
        setFilteredAdmins((t) => {
            t.sort((a, b) => sortUser(a, b, orderBy, sortDirection))
            return [...t]
        })
    }, [orderBy, sortDirection])

    useEffect(() => {
        setIsAdminValid(
            profilePic !== null &&
            name !== "" &&
            surname !== "" &&
            email !== "" &&
            email.match(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/) &&
            (!selectedCompany || !!selectedCompanyRole)
        )
    }, [profilePic, name, surname, email, selectedCompany, selectedCompanyRole])

    const addAdmin = useCallback(async () => {
        setAddStatus(ButtonStatus.Loading)
        try {
            const formData = new FormData()
            formData.append("name", name)
            formData.append("surname", surname)
            formData.append("email", email)
            formData.append("status", status)
            formData.append("role", roleOptions[role.id].value)
            if (selectedCompany?.value) {
                formData.append("company_id", selectedCompany.value)
                formData.append("company_role", selectedCompanyRole?.value)
            }

            if (profilePic) {
                let blob = await fetch(profilePic).then(r => r.blob());
                formData.append("picture", blob);
            }

            const admin = await api.post("/admin/admins", formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            })
            setAdmins(t => [...t, admin])
            setAddStatus(ButtonStatus.Success)
            setOpen(false)
            setProfilePic(null)
            setName("")
            setSurname("")
            setEmail("")
            setRole(roleOptions[0])
            setSelectedCompany(null)
            setSelectedCompanyRole(null)
            setAddStatus(null)
            getAdmins()
        } catch (e) {
            console.error(e)
            setAddStatus(ButtonStatus.Error)
        }
    }, [name, surname, email, profilePic, status, role, roleOptions, selectedCompany])

    const onAddClick = () => setOpen(true)

    useImperativeHandle(ref, () => {
        return {
            onAddClick
        }
    })

    const onSearch = useCallback(async (text) => {
        text = text.toLowerCase()
        setFilteredAdmins(
            admins.filter(t => (
                (t.name.trim().toLowerCase() +
                    t.surname.trim().toLowerCase()).includes(text.replaceAll(' ', '').toLowerCase()) ||
                (t.surname.trim().toLowerCase() +
                    t.name.trim().toLowerCase()).includes(text.replaceAll(' ', '').toLowerCase())
            ))
        )
    }, [admins])

    const getAdminStatusBadge = (status) => {
        switch (status) {
            case 'active':
                return <StatusBadge appearance="soft" label={"ATTIVO"} color={"var(--sf-green)"} />
            case 'suspended':
                return <StatusBadge appearance="soft" label={"SOSPESO"} color={"var(--sf-orange)"} />
            default:
                return null
        }
    }


    return (
        <div className={styles.container}>
            <div className={styles.section}>
                <div className={styles.sectionInner}>
                    <div className={styles.admins}>
                        <div className={styles.searchBar}>
                        <TextInput
                            onKeyUp={onSearch}
                            type="search" appearance="modern"
                            placeholder={t("search")}
                            style={{
                                backgroundColor: "var(--background-secondary-color)",

                            }} />
                        </div>
                        <Table appearance="minimal" style={{ borderRadius: 0, marginTop: 0 }} columns={[
                            { key: 'id', label: 'ID', type: 'number' },
                            { key: 'name', label: 'NOME', type: 'string' },
                            { key: 'surname', label: 'COGNOME', type: 'string' },
                            { key: 'email', label: 'EMAIL', type: 'string' },
                            { key: 'company', label: 'AZIENDA', type: 'string' },
                            { key: 'role', label: 'RUOLO', type: 'string' },
                            { key: 'status', label: 'STATO', type: 'custom' },
                            { key: 'registered_at', label: 'REGISTRATO IL', type: 'datetime' },
                            { key: 'last_access', label: 'ULTIMO ACCESSO', type: 'datetime' }
                        ]}
                            loading={adminsLoading}
                            elements={filteredAdmins.map(t => ({
                                ...t,
                                company: t?.company?.name || 'N/A',
                                role: t.role.toUpperCase(),
                                status: { value: t.status, type: 'string', component: getAdminStatusBadge(t.status) },
                                href: `/users/admins/${t.id}`
                            }))}
                            defaultSort={{ key: 'registered_at', direction: 'desc' }}
                        />
                    </div>
                </div>

            </div>
            <Dialog
                appearance="modern"
                style={{ maxWidth: 840 }}
                open={open}
                onClose={() => {
                    setOpen(false)
                    setProfilePic(null)
                    setName("")
                    setSurname("")
                    setEmail("")
                    setRole(AdminRole.Main)
                    setSelectedCompany(null)
                    setSelectedCompanyRole(null)
                    setAddStatus(null)
                }}
                title={`Aggiungi Admin`}
            >
                <div className={formStyles.dialog}>
                    <div className={formStyles.imageSelector}>
                        {!cropMode && !profilePic &&
                            <Dropzone
                                onDrop={(files) => {
                                    if (files[0]) {
                                        setImgFile(files[0])
                                        setCropMode(true)
                                    }
                                }}
                                accept={{
                                    'image/jpeg': [],
                                    'image/png': [],
                                    'image/heic': []
                                }}
                            >
                                {({ getRootProps, getInputProps }) => (
                                    <section style={{ display: 'flex', width: '100%', }}>
                                        <div {...getRootProps()} style={{ display: 'flex', height: "100%", alignItems: "center", width: '100%' }}>
                                            <input {...getInputProps()} ref={dropzoneRef} />
                                            <div className={formStyles.editPictureDropzone}>
                                                <InsertImageIllustration></InsertImageIllustration>
                                                <div className={formStyles.editPictureLabel}>
                                                    {t('profile.dropImage')}
                                                </div>
                                                <Button
                                                    style={{ marginTop: '.5rem', padding: '0.6rem 2rem' }}
                                                    appearance="subtle" accentColor="var(--primary)"
                                                    onClick={() => dropzoneRef.current?.click()}
                                                >
                                                    {capitalize(t('profile.chooseImage'))}
                                                </Button>
                                            </div>
                                        </div>
                                    </section>
                                )}
                            </Dropzone>
                        }
                        {cropMode && !profilePic &&
                            <>
                                <ImageCropper
                                    src={URL.createObjectURL(imgFile)}
                                    onSave={(image) => {
                                        setProfilePic(image)
                                        setCropMode(false)
                                    }}
                                    onCancel={() => { setCropMode(false) }}
                                    compression={{ quality: 0.7, maxWidth: 300, maxHeight: 300 }}
                                >
                                </ImageCropper>
                            </>
                        }
                        {
                            profilePic &&
                            <div className={formStyles.profilePicPreview}>
                                <img className={formStyles.profilePic} src={profilePic} alt='' />
                                <Button
                                    style={{ padding: "0.7rem 1.5rem" }}
                                    appearance="subtle" accentColor="var(--primary)"
                                    onClick={() => {
                                        setProfilePic(null)
                                        setCropMode(false)
                                    }}
                                >
                                    <EditIcon />
                                    Cambia
                                </Button>
                            </div>
                        }
                    </div>
                    <div className={formStyles.inputs}>
                        <div className={formStyles.field}><span className={formStyles.label}>Ruolo</span><Select appearance="modern" portal selected={role} options={roleOptions} onSelect={(r) => setRole(r)} /></div>
                        <div className={formStyles.field}><span className={formStyles.label}>Azienda</span><Select appearance="modern" portal
                            selected={selectedCompany}
                            options={companies}
                            nullable
                            nullLabel="Nessuna azienda"
                            placeholder="Azienda"
                            onSelect={(c) => {
                                const nextCompany = c?.id === -1 ? null : c
                                setSelectedCompany(nextCompany)
                                if (!nextCompany) {
                                    setSelectedCompanyRole(null)
                                }
                            }}
                        /></div>
                        {selectedCompany &&
                            <div className={formStyles.field}><span className={formStyles.label}>Ruolo aziendale</span><Select appearance="modern" portal
                                selected={selectedCompanyRole}
                                options={companyRoleOptions}
                                placeholder="Ruolo aziendale *"
                                onSelect={(r) => setSelectedCompanyRole(r)}
                            /></div>
                        }
                        <TextInput appearance="modern" value={name} type="text" label={capitalize(t('profile.name'))} placeholder={capitalize(t('profile.name'))} onKeyUp={v => setName(v)}></TextInput>
                        <TextInput appearance="modern" value={surname} type="text" label={capitalize(t('profile.surname'))} placeholder={capitalize(t('profile.surname'))} onKeyUp={v => setSurname(v)}></TextInput>
                        <TextInput appearance="modern" value={email} type="text" label={capitalize(t('profile.email'))} placeholder={capitalize(t('profile.email'))} onKeyUp={v => setEmail(v)} />

                        <div className={formStyles.bottom}>
                            <div className={formStyles.switch}>
                                <button type="button" aria-pressed={status === AccountStatus.Active} className={status === AccountStatus.Active ? `${formStyles.status} ${formStyles.active}` : `${formStyles.status} ${formStyles.active} ${formStyles.notSelected}`}
                                    onClick={() => setStatus(AccountStatus.Active)}>Attivo</button>
                                <button type="button" aria-pressed={status === AccountStatus.Suspended} className={status === AccountStatus.Suspended ? `${formStyles.status} ${formStyles.suspended}` : `${formStyles.status} ${formStyles.suspended} ${formStyles.notSelected}`}
                                    onClick={() => setStatus(AccountStatus.Suspended)}>Sospeso</button>
                            </div>

                            <Button
                                style={{ padding: '.65rem 1rem', borderRadius: 8, fontSize: '.75rem' }}
                                accentColor={"var(--primary)"}
                                disabled={isAdminValid === false || addStatus === ButtonStatus.Loading}
                                status={addStatus}
                                onClick={addAdmin}
                            >
                                Aggiungi
                                {
                                    addStatus === null &&
                                    <AddIcon />
                                }
                            </Button>
                        </div>
                    </div>
                </div>
            </Dialog>
        </div >
    )
})

export default Admins
